import {FieldErrors, FieldPath, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Form} from "@/components/ui/form.tsx";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs.tsx";
import {Character} from "@/characters/model/Character.ts";
import {
    CharacterFormInput,
    CharacterFormOutput,
    characterSchema,
    SaveCharacterRequest,
    toCharacterFormValues,
    toSaveCharacterRequest,
} from "@/characters/admin/form/CharacterFormSchema.ts";
import CharacterActionsForm from "@/characters/admin/form/CharacterActionsForm.tsx";
import CharacterBasicsForm from "@/characters/admin/form/CharacterBasicsForm.tsx";
import CharacterDefenseForm from "@/characters/admin/form/CharacterDefenseForm.tsx";
import CharacterSpellsForm from "@/characters/admin/form/CharacterSpellsForm.tsx";
import {ReactNode, useEffect, useState} from "react";
import {SaveState} from "@/components/buttons/SaveButton.tsx";
import {useTemporaryImageFormField} from "@/images/useTemporaryImageFormField.ts";
import FormToolbar from "@/components/management/FormToolbar.tsx";
import CharacterStatsForm from "@/characters/admin/form/CharacterStatsForm.tsx";
import CharacterCapabilitiesForm from "@/characters/admin/form/CharacterCapabilitiesForm.tsx";
import CharacterDamageModifiersForm from "@/characters/admin/form/CharacterDamageModifiersForm.tsx";
import {CircleAlert, Shield, Sparkles, Swords, UserRound} from "lucide-react";
import {notifyError} from "@/api/ErrorNotification.ts";

type CharacterFormSection = "basics" | "defense" | "actions" | "spells";

const SECTION_FIELDS: Record<CharacterFormSection, ReadonlySet<string>> = {
    basics: new Set(["id", "name", "image", "hostile", "type", "race", "alignment", "size", "classes", "challenge", "xp", "source"]),
    defense: new Set(["hitPoints", "armorClass", "speeds", "attributes", "damageResistances", "damageVulnerabilitys", "damageImmunitys", "conditionImmunitys", "senses", "languages", "skills"]),
    actions: new Set(["actions"]),
    spells: new Set(["spellcasting"]),
};

export default function CharacterForm({defaultValue, onSubmit, onCancel, onDirtyChange}: {
    defaultValue?: Character;
    onSubmit: (character: SaveCharacterRequest) => Promise<Character>;
    onCancel: () => void;
    onDirtyChange: (dirty: boolean) => void;
}) {

    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState<string>();
    const [activeSection, setActiveSection] = useState<CharacterFormSection>("basics");
    const form = useForm<CharacterFormInput, unknown, CharacterFormOutput>({
        resolver: zodResolver(characterSchema),
        mode: "onBlur",
        defaultValues: toCharacterFormValues(defaultValue),
    });
    const imageField = useTemporaryImageFormField({
        form,
        name: "image",
        onUploadStart: () => setError(undefined),
    });

    useEffect(() => onDirtyChange(form.formState.isDirty), [form.formState.isDirty, onDirtyChange]);

    const onValid = async (data: CharacterFormOutput) => {
        setSaveState("saving");
        setError(undefined);
        try {
            const saved = await onSubmit(toSaveCharacterRequest(data));
            imageField.commit();
            form.reset(toCharacterFormValues(saved));
            setSaveState("success");
        } catch (reason) {
            setSaveState("error");
            notifyError(reason, "Der Charakter konnte nicht gespeichert werden.");
        } finally {
            setTimeout(() => setSaveState("idle"), 1500);
        }
    }

    const onInvalid = (errors: FieldErrors<CharacterFormInput>) => {
        console.error("Form errors", errors);
        setSaveState("error");
        setError("Bitte überprüfe die markierten Eingaben.");
        setTimeout(() => setSaveState("idle"), 1500);
        const errorPath = findFirstErrorPath(errors);
        if (errorPath) {
            setActiveSection(sectionForField(errorPath.split(".")[0]));
            setTimeout(() => form.setFocus(errorPath as FieldPath<CharacterFormInput>));
        }
    };

    const hasErrors = (section: CharacterFormSection) =>
        Object.keys(form.formState.errors).some(field => SECTION_FIELDS[section].has(field));


    return (<Form {...form}>
            <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="flex h-full min-h-0 flex-col gap-2">
                <FormToolbar title={defaultValue ? "Charakter bearbeiten" : "Neuer Charakter"}
                             subtitle="Globaler Charakterkatalog"
                             saveState={saveState}
                             saveDisabled={!form.formState.isDirty || imageField.uploading || saveState === "saving"}
                             onCancel={onCancel}/>
                {error && <p role="alert"
                             className="shrink-0 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                </p>}

                <Tabs value={activeSection}
                      onValueChange={value => setActiveSection(value as CharacterFormSection)}
                      className="min-h-0 flex-1 gap-0 overflow-hidden rounded-lg border bg-card lg:grid lg:grid-cols-[13rem_minmax(0,1fr)]">
                    <TabsList className="h-auto w-full shrink-0 justify-start gap-1 overflow-x-auto rounded-none border-x-0 border-t-0 p-2 lg:h-full lg:flex-col lg:justify-start lg:overflow-visible lg:border-y-0 lg:border-l-0 lg:p-3">
                        <SectionTrigger value="basics" label="Grundlagen" hasError={hasErrors("basics")} icon={<UserRound/>}/>
                        <SectionTrigger value="defense" label="Werte & Verteidigung" hasError={hasErrors("defense")} icon={<Shield/>}/>
                        <SectionTrigger value="actions" label="Aktionen" hasError={hasErrors("actions")} icon={<Swords/>}/>
                        <SectionTrigger value="spells" label="Zauberwirken" hasError={hasErrors("spells")} icon={<Sparkles/>}/>
                    </TabsList>
                    <div className="min-h-0 overflow-auto p-3 md:p-4">
                        <TabsContent value="basics" className="m-0 space-y-8">
                            <CharacterBasicsForm character={defaultValue}
                                                 image={imageField.image}
                                                 uploadingImage={imageField.uploading}
                                                 onImageSelected={imageField.onFileSelected}/>
                        </TabsContent>
                        <TabsContent value="defense" className="m-0 space-y-8">
                            <CharacterDefenseForm/>
                            <CharacterStatsForm/>
                            <CharacterDamageModifiersForm/>
                            <CharacterCapabilitiesForm/>
                        </TabsContent>
                        <TabsContent value="actions" className="m-0">
                            <CharacterActionsForm/>
                        </TabsContent>
                        <TabsContent value="spells" className="m-0">
                            <CharacterSpellsForm/>
                        </TabsContent>
                    </div>
                </Tabs>

            </form>
        </Form>
    );
}

function SectionTrigger({value, label, hasError, icon}: {
    value: CharacterFormSection;
    label: string;
    hasError: boolean;
    icon: ReactNode;
}) {
    return <TabsTrigger value={value}
                        className="h-9 flex-none justify-start px-3 lg:w-full">
        {icon}
        <span>{label}</span>
        {hasError && <CircleAlert className="ml-auto text-destructive" aria-label="Enthält Fehler"/>}
    </TabsTrigger>;
}

function sectionForField(field: string): CharacterFormSection {
    const sections = Object.entries(SECTION_FIELDS) as [CharacterFormSection, ReadonlySet<string>][];
    const section = sections.find(([, fields]) => fields.has(field));
    return section?.[0] ?? "basics";
}

function findFirstErrorPath(errors: FieldErrors<CharacterFormInput>, prefix = ""): string | undefined {
    for (const [field, error] of Object.entries(errors)) {
        if (!error) continue;
        const path = prefix ? `${prefix}.${field}` : field;
        if (field === "root") return prefix || undefined;
        if (typeof error === "object" && ("message" in error || "type" in error || "ref" in error)) return path;
        if (typeof error === "object") {
            const nested = findFirstErrorPath(error as FieldErrors<CharacterFormInput>, path);
            if (nested) return nested;
        }
    }
}
